'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { LogoAnimated } from './logo-animated';

const navigation = [
  { name: 'Servizi', href: '/#soluzioni' },
  { name: 'News', href: '/news' },
  { name: 'Contatti', href: '/contatti' },
];

// Emil: strong ease-out — starts fast, feels responsive
const EASE = [0.23, 1, 0.32, 1] as const;

type HeaderProps = {
  headerTitle?: string;
};

// ── Magnetic nav link (pill hover) ───────────────────────────────────────────

function PillLink({
  item,
  isActive,
  onClick,
}: {
  item: (typeof navigation)[0];
  isActive: boolean;
  onClick?: () => void;
}) {
  return (
    <Link
      href={item.href}
      onClick={onClick}
      className={`relative block rounded-full px-4 py-2 text-sm font-medium transition-colors duration-150 active:scale-[0.97] select-none ${
        isActive
          ? 'text-white'
          : 'text-gray-400 hover:text-white hover:bg-white/[0.06]'
      }`}
    >
      {/* Shared active background — layout-animated */}
      {isActive && (
        <motion.span
          layoutId="pill-active"
          className="absolute inset-0 rounded-full bg-white/[0.09]"
          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
        />
      )}
      <span className="relative z-10">{item.name}</span>
    </Link>
  );
}

// ── Main header ───────────────────────────────────────────────────────────────

export default function Header({ headerTitle }: HeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();
  const showNav = !headerTitle;
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Passive scroll — only updates boolean state, never CSS
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Focus close button on open
  useEffect(() => {
    if (mobileOpen) {
      const t = setTimeout(() => closeButtonRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [mobileOpen]);

  // Escape to close + simple focus trap
  useEffect(() => {
    if (!mobileOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = document.getElementById('mobile-nav-panel');
      if (!panel) return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const isActive = useCallback(
    (href: string) => {
      const path = href.split('#')[0];
      if (path === '' || path === '/') return pathname === '/';
      return pathname.startsWith(path);
    },
    [pathname]
  );

  return (
    <>
      {/* ── Top gradient — improves logo readability on any background ── */}
      <motion.div
        className="pointer-events-none fixed inset-x-0 top-0 z-40 h-44 bg-gradient-to-b from-black/70 to-transparent"
        animate={{ opacity: scrolled ? 0 : 1 }}
        transition={{ duration: 0.4 }}
      />

      {/* ── Logo bar — always fixed, fades on scroll ── */}
      <div className="pointer-events-none fixed inset-x-0 top-0 z-50 flex items-center justify-between px-6 py-5 lg:px-10">
        {/* Logo */}
        <div className="pointer-events-auto">
          <Link href="/" className="-m-1 block p-1" aria-label="Torna alla homepage">
            <LogoAnimated scrolled={scrolled} />
          </Link>
        </div>

        {/* Right corner */}
        <div className="pointer-events-auto flex items-center gap-3">
          {showNav && (
            <>
              {/* Desktop login — visible before scroll, fades when pill appears */}
              <motion.div
                className="hidden lg:block"
                animate={{ opacity: scrolled ? 0 : 1 }}
                transition={{ duration: 0.2 }}
                style={{ pointerEvents: scrolled ? 'none' : 'auto' }}
              >
                <Link
                  href="/login"
                  className="text-sm font-medium text-gray-500 transition-colors duration-150 hover:text-white"
                >
                  Log in <span aria-hidden="true">→</span>
                </Link>
              </motion.div>

              {/* Mobile burger */}
              <button
                type="button"
                className="lg:hidden -m-2 p-2 text-gray-400 transition-colors hover:text-white"
                onClick={() => setMobileOpen(true)}
                aria-label="Apri menu principale"
                aria-expanded={mobileOpen}
                aria-controls="mobile-nav-panel"
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </button>
            </>
          )}

          {/* Optional page title (gestionale pages) */}
          {headerTitle && (
            <span className="text-sm font-semibold text-white">{headerTitle}</span>
          )}
        </div>
      </div>

      {/* ── Floating pill — desktop, appears on scroll ── */}
      {showNav && (
        <AnimatePresence>
          {scrolled && (
            <motion.nav
              aria-label="Navigazione principale"
              initial={{ opacity: 0, y: -18, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -18, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 340, damping: 28, mass: 0.75 }}
              className="pointer-events-auto fixed left-1/2 top-4 z-50 hidden -translate-x-1/2 lg:block"
            >
              <div className="flex items-center gap-1 rounded-full border border-white/[0.09] bg-gray-950/92 p-1.5 shadow-2xl shadow-black/40 backdrop-blur-2xl">
                {/* Nav links */}
                <div className="flex items-center gap-0.5">
                  {navigation.map((item, i) => (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.04 + 0.05, duration: 0.18 }}
                    >
                      <PillLink item={item} isActive={isActive(item.href)} />
                    </motion.div>
                  ))}
                </div>

                {/* Separator */}
                <div className="mx-1 h-4 w-px bg-white/10" aria-hidden="true" />

                {/* Login CTA — high contrast inside pill */}
                <Link
                  href="/login"
                  className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-gray-900 transition-all duration-150 hover:bg-gray-100 active:scale-[0.97]"
                >
                  Log in
                </Link>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      )}

      {/* ── Mobile menu ── */}
      <AnimatePresence>
        {mobileOpen && showNav && (
          <>
            {/* Backdrop */}
            <motion.div
              aria-hidden="true"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
              className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
            />

            {/* Side panel — spring slide-in */}
            <motion.div
              id="mobile-nav-panel"
              role="dialog"
              aria-modal="true"
              aria-label="Menu di navigazione"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 290, damping: 29, mass: 0.85 }}
              className="fixed bottom-0 right-0 top-0 z-50 flex w-[82vw] max-w-xs flex-col border-l border-white/[0.07] bg-gray-950 px-6 py-6"
            >
              {/* Panel header */}
              <div className="mb-10 flex items-center justify-between">
                <Image
                  src="/logo-refineed.svg"
                  alt="Refineed"
                  width={96}
                  height={26}
                  className="h-6 w-auto opacity-40"
                />
                <button
                  ref={closeButtonRef}
                  type="button"
                  className="-m-2 p-2 text-gray-600 transition-colors hover:text-white"
                  onClick={() => setMobileOpen(false)}
                  aria-label="Chiudi menu"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>

              {/* Nav items — staggered in */}
              <nav className="flex-1" aria-label="Menu mobile">
                <ul className="space-y-0" role="list">
                  {navigation.map((item, i) => {
                    const active = isActive(item.href);
                    return (
                      <motion.li
                        key={item.name}
                        initial={{ opacity: 0, x: 18 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.05 + i * 0.06, duration: 0.3, ease: EASE }}
                      >
                        <Link
                          href={item.href}
                          onClick={() => setMobileOpen(false)}
                          className={`flex items-center justify-between border-b border-white/[0.05] py-5 text-xl font-semibold transition-colors duration-150 ${
                            active ? 'text-white' : 'text-gray-500 hover:text-white'
                          }`}
                        >
                          {item.name}
                          <motion.span
                            className={`text-sm ${active ? 'text-primary' : 'text-gray-700'}`}
                            aria-hidden="true"
                          >
                            →
                          </motion.span>
                        </Link>
                      </motion.li>
                    );
                  })}
                </ul>
              </nav>

              {/* Bottom CTAs */}
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.22, duration: 0.35, ease: EASE }}
                className="mt-8 space-y-3"
              >
                <Link
                  href="/contatti"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-150 active:scale-[0.97]"
                >
                  Parliamone
                </Link>
                <Link
                  href="/login"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center rounded-full border border-white/[0.08] px-6 py-3.5 text-sm font-medium text-gray-500 transition-colors duration-150 hover:text-white"
                >
                  Log in
                </Link>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
